import {FC, useEffect, useRef, useState} from 'react';
import {Box, IconButton, Popper, Typography} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import {ReactComponent as CloseIcon} from '../../../../assets/images/close-base-icon.svg';
import './FilterSelectionModal.scss';
import ArrRadio from '../../../common/arr-radio/ArrRadio';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrDatePicker from '../../../common/arr-date-picker/ArrDatePicker';
import {
  ActiveFilter,
  filterSelectionType,
} from '../doc-filter-accordion/DocExplorerFilterAccordion';
import {t} from 'i18next';
import LazyLoadFilter from './LazyLoadFilter';

export const FILTER_SELECTION_MAP: {[key: string]: string} = {
  between: 'Between',
  before: 'Before',
  after: 'After',
  on: 'On',
  include: 'Include',
  exclude: 'Exclude',
};

type FilterSelectionModalProps = {
  filterSelectionData: ActiveFilter;

  anchorElement: HTMLElement | null;
  onAddToFilters: (filterData: ActiveFilter) => void;
  onClose: () => void;
  closeOnEsc?: boolean;
};
const FilterSelectionModal: FC<FilterSelectionModalProps> = ({
  filterSelectionData,
  anchorElement,
  onAddToFilters,
  onClose,
  closeOnEsc,
}) => {
  const [filterData, setFilterData] = useState<ActiveFilter>(
    {} as ActiveFilter
  );
  const [isAddToFiltersDisabled, setIsAddToFiltersDisabled] = useState(true);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab') return;
    const root = modalRef.current;
    const closeBtn = closeButtonRef.current;
    if (!root || !closeBtn) return;

    // focusable selectors excluding disabled elements
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>(focusableSelector)
    ).filter((el) => el.offsetParent !== null);
    if (nodes.length === 0) {
      event.preventDefault();
      closeBtn.focus();
      return;
    }

    const active = document.activeElement as HTMLElement | null;
    const idx = active ? nodes.indexOf(active) : -1;
    const lastEl = nodes[nodes.length - 1];

    if (event.shiftKey) {
      if (closeBtn.contains(active)) {
        event.preventDefault();
        lastEl.focus();
        return;
      }

      if (idx === nodes.length - 1) {
        return;
      }
    }

    if (idx === nodes.length - 1) {
      event.preventDefault();
      closeBtn.focus();
    }
  };

  useEffect(() => {
    if (filterData.type == 'NVARCHAR' && !filterData.values?.length) {
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'DATETIME' &&
      filterData.selectionType != 'between' &&
      !filterData.startDate
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (
      filterData.type == 'DATETIME' &&
      filterData.selectionType == 'between' &&
      (!filterData.startDate || !filterData.endDate)
    ) {
      setIsAddToFiltersDisabled(true);
    } else if (Object.keys(filterData).length) {
      setIsAddToFiltersDisabled(false);
    }
  }, [filterData]);
  useEffect(() => {
    const incomingFilterData = {...filterSelectionData};
    if (
      filterSelectionData.type == 'NVARCHAR' &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'include';
    }
    if (
      filterSelectionData.type == 'DATETIME' &&
      !filterSelectionData.selectionType
    ) {
      incomingFilterData.selectionType = 'between';
    }
    setFilterData(incomingFilterData);
  }, [filterSelectionData]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEsc && !isDropdownOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc, true);

    return () => {
      window.removeEventListener('keydown', handleEsc, true);
    };
  }, [onClose, closeOnEsc, isDropdownOpen]);

  const radioButtonContainer = (
    <Box className="radio-button-container">
      <Box>
        <ArrRadio
          dataTestId="include-radio-button"
          aria-label={t('docExplorer.filterAccordion.include')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('docExplorer.filterAccordion.include'),
              value: 'include',
            },
          ]}
          value={filterData.selectionType ?? ''}
          selected={false}
          onChange={() => {
            setFilterData((prev) => {
              return {...prev, selectionType: 'include'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
      <Box>
        <ArrRadio
          dataTestId="exclude-radio-button"
          aria-label={t('docExplorer.filterAccordion.exclude')}
          className="radio-button"
          variant="secondary"
          size="xs"
          name="selectionType"
          data={[
            {
              label: t('docExplorer.filterAccordion.exclude'),
              value: 'exclude',
            },
          ]}
          value={filterData.selectionType ?? ''}
          selected={false}
          onChange={() => {
            setFilterData((prev) => {
              return {...prev, selectionType: 'exclude'};
            });
          }}
          uncheckedTabIndex={0}
          labelTabIndex={-1}
        />
      </Box>
    </Box>
  );
  const multiSelectFilterContainer = (
    <Box>
      <LazyLoadFilter
        setFilterData={setFilterData}
        filterData={filterData}
        id={filterSelectionData?.id}
        onDropdownToggle={setIsDropdownOpen}
      />
    </Box>
  );
  const singleSelectDateSelectionTypeContainer = (
    <Box>
      <ArrSingleSelectDropdown
        dataTestId="asd-filterAccordion-single-select-filter-dropdown"
        aria-label={t('docExplorer.filterAccordion.dateRange')}
        dropdownLabel={t('docExplorer.filterAccordion.dateRange')}
        dropdownItemKey="value"
        dropdownItemDisplay="label"
        dropdownItems={[
          {value: 'between', label: t('docExplorer.filterAccordion.between')},
          {value: 'before', label: t('docExplorer.filterAccordion.before')},
          {value: 'after', label: t('docExplorer.filterAccordion.after')},
          {value: 'on', label: t('docExplorer.filterAccordion.on')},
        ]}
        selectedValues={{
          value: filterData.selectionType,
          label: FILTER_SELECTION_MAP[filterData.selectionType ?? 'between'],
        }}
        onSelectionChange={(value) => {
          setFilterData((prev) => {
            return {
              ...prev,
              selectionType: value?.value as filterSelectionType,
              startDate: '',
              endDate: '',
            };
          });
        }}
        tabIndex={-1}
        searchable
      />
    </Box>
  );
  const singleDateContainer = (
    <Box>
      <ArrDatePicker
        dataTestId={`date-picker-filter-${filterData.selectionType}`}
        aria-label={t('docExplorer.filterAccordion.startDate')}
        type="date-picker"
        label={t('docExplorer.filterAccordion.startDate')}
        initialStartDate={filterData.startDate}
        placeholder={t('docExplorer.filterAccordion.datePlaceholder')}
        onChange={(date) => {
          setFilterData((prev) => {
            return {...prev, startDate: date as string};
          });
        }}
        showToday={true}
        defaultError={false}
      />
    </Box>
  );
  const multiDateContainer = (
    <Box className="multi-date-container">
      <Box>
        <ArrDatePicker
          dataTestId={`start-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('docExplorer.filterAccordion.startDate')}
          type="date-picker"
          label={t('docExplorer.filterAccordion.startDate')}
          initialStartDate={filterData.startDate}
          placeholder="YYYY/MM/DD"
          onChange={(date) => {
            setFilterData((prev) => {
              return {...prev, startDate: date as string};
            });
          }}
          showToday={true}
          defaultError={false}
        />
      </Box>
      <Box>
        <ArrDatePicker
          dataTestId={`end-date-picker-filter-${filterData.selectionType}`}
          aria-label={t('docExplorer.filterAccordion.endDate')}
          type="date-picker"
          label={t('docExplorer.filterAccordion.endDate')}
          initialStartDate={filterData.endDate}
          placeholder="YYYY/MM/DD"
          onChange={(date) => {
            setFilterData((prev) => {
              return {...prev, endDate: date as string};
            });
          }}
          showToday={true}
          defaultError={false}
        />
      </Box>
    </Box>
  );
  return (
    <Popper
      data-testid="doc-filter-selection-modal"
      open={Boolean(anchorElement)}
      anchorEl={anchorElement}
      placement="bottom"
    >
      <Box
        className="filter-selection-modal"
        ref={modalRef}
        onKeyDown={handleKeyDown}
      >
        <Box className="modal-header">
          <Typography
            className="typo-text subhead4_size title"
            data-testid={`filter-selection-modal-filter-by`}
          >
            {t('docExplorer.filterAccordion.filterBy', {
              label: filterData.label,
            })}
          </Typography>
          <Box>
            <IconButton
              data-testid="filter-selection-close-button"
              aria-label={t('common.close')}
              size="small"
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Tab' && e.shiftKey) return;
              }}
              className="modal-close"
              ref={closeButtonRef}
            >
              <CloseIcon className="close-icon" />
            </IconButton>
          </Box>
        </Box>
        <Box className="primary-selection">
          {filterData.type == 'NVARCHAR' && radioButtonContainer}
          {filterData.type == 'DATETIME' &&
            singleSelectDateSelectionTypeContainer}
        </Box>
        <Box className="secondery-selection">
          {(filterData.selectionType == 'include' ||
            filterData.selectionType == 'exclude') &&
            multiSelectFilterContainer}

          {(filterData.selectionType == 'before' ||
            filterData.selectionType == 'after' ||
            filterData.selectionType == 'on') &&
            singleDateContainer}

          {filterData.selectionType == 'between' && multiDateContainer}
        </Box>
        <Box className="modal-submit">
          <ArrButton
            dataTestId="filter-selection-modal-submit"
            aria-label={t('docExplorer.filterAccordion.addToFilters')}
            size="small"
            disabled={isAddToFiltersDisabled}
            isAriaDisabled={isAddToFiltersDisabled}
            onClick={() => {
              onAddToFilters(filterData);
            }}
          >
            {t('docExplorer.filterAccordion.addToFilters')}
          </ArrButton>
        </Box>
      </Box>
    </Popper>
  );
};

export default FilterSelectionModal;
